import { defineStore } from 'pinia'
import type { Product, ProductConfiguration, SpecificationPart } from '../types'

const STORAGE_KEY = 'cabinet-customization-draft-v1'

export interface CustomizationContact {
  contactName: string
  wechatId: string
  notes: string
}

interface CustomizationDraft {
  slug: string
  productId: string
  productVersionId: string
  specificationId: string
  configuration: ProductConfiguration
  contact: CustomizationContact
  updatedAt: string
}

interface CustomizationState {
  draft: CustomizationDraft | null
}

export function emptyCustomizationContact(): CustomizationContact {
  return {
    contactName: '',
    wechatId: '',
    notes: '',
  }
}

function normalizeCustomizationContact(value?: Partial<CustomizationContact>): CustomizationContact {
  return {
    contactName: String(value?.contactName || ''),
    wechatId: String(value?.wechatId || ''),
    notes: String(value?.notes || ''),
  }
}

function readDraft(): CustomizationDraft | null {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (!stored) return null
    const parsed = JSON.parse(stored) as Partial<CustomizationDraft>
    if (!parsed.slug || !parsed.productVersionId || !parsed.configuration) return null
    return {
      slug: String(parsed.slug),
      productId: String(parsed.productId || parsed.configuration.productId || ''),
      productVersionId: String(parsed.productVersionId),
      specificationId: String(parsed.specificationId || parsed.configuration.specificationId || ''),
      configuration: {
        ...parsed.configuration,
        partColors: { ...(parsed.configuration.partColors || {}) },
      },
      contact: normalizeCustomizationContact(parsed.contact),
      updatedAt: String(parsed.updatedAt || ''),
    }
  } catch {
    sessionStorage.removeItem(STORAGE_KEY)
    return null
  }
}

function allowedColorsForPart(product: Product, part: SpecificationPart) {
  const productCodes = new Set(product.colors.map((color) => color.code))
  return part.allowedColorCodes.length
    ? part.allowedColorCodes.filter((code) => productCodes.has(code))
    : [...productCodes]
}

function defaultConfiguration(product: Product): ProductConfiguration {
  const profile = product.customizationProfile
  const parts = profile?.specification.parts || []
  const partColors = Object.fromEntries(parts.map((part) => {
    const allowed = allowedColorsForPart(product, part)
    const colorCode = allowed.includes(part.defaultColorCode)
      ? part.defaultColorCode
      : allowed[0] || ''
    return [part.id, colorCode]
  }))
  return {
    productId: product.id,
    productVersionId: product.versionId,
    specificationId: profile?.specificationId || '',
    skuCode: '',
    colorCode: '',
    partColors,
    hardwareCode: product.hardwarePackages.find((item) => item.isDefault)?.code
      || product.hardwarePackages[0]?.code
      || '',
    widthMm: 0,
    heightMm: 0,
    depthMm: 0,
  }
}

function normalizeConfiguration(
  product: Product,
  source?: ProductConfiguration,
): ProductConfiguration {
  const next = defaultConfiguration(product)
  const profile = product.customizationProfile
  if (!source || !profile) return next

  const hardwareCodes = new Set(product.hardwarePackages.map((item) => item.code))
  if (hardwareCodes.has(source.hardwareCode)) next.hardwareCode = source.hardwareCode
  next.widthMm = Number.isFinite(source.widthMm) && source.widthMm > 0 ? source.widthMm : 0
  next.heightMm = Number.isFinite(source.heightMm) && source.heightMm > 0 ? source.heightMm : 0
  next.depthMm = Number.isFinite(source.depthMm) && source.depthMm > 0 ? source.depthMm : 0
  profile.specification.parts.forEach((part) => {
    const allowed = allowedColorsForPart(product, part)
    const stored = String(source.partColors?.[part.id] || '')
    if (stored && allowed.includes(stored)) next.partColors[part.id] = stored
    else if (!part.required) next.partColors[part.id] = ''
  })
  return next
}

export const useCustomizationStore = defineStore('customization', {
  state: (): CustomizationState => ({ draft: readDraft() }),
  actions: {
    persist() {
      if (typeof sessionStorage === 'undefined') return
      if (!this.draft) {
        sessionStorage.removeItem(STORAGE_KEY)
        return
      }
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.draft))
    },
    configurationFor(product: Product): ProductConfiguration {
      const profile = product.customizationProfile
      if (!profile) return defaultConfiguration(product)
      const sameVersion = this.draft
        && this.draft.slug === product.slug
        && this.draft.productId === product.id
        && this.draft.productVersionId === product.versionId
        && this.draft.specificationId === profile.specificationId
      if (!sameVersion && this.draft?.slug === product.slug) this.clear()
      return normalizeConfiguration(
        product,
        sameVersion ? this.draft?.configuration : undefined,
      )
    },
    contactFor(product: Product): CustomizationContact {
      const sameVersion = this.draft
        && this.draft.slug === product.slug
        && this.draft.productVersionId === product.versionId
      return sameVersion
        ? normalizeCustomizationContact(this.draft!.contact)
        : emptyCustomizationContact()
    },
    save(
      product: Product,
      configuration: ProductConfiguration,
      contact?: CustomizationContact,
    ) {
      const profile = product.customizationProfile
      if (!profile) return
      const existingContact = this.contactFor(product)
      this.draft = {
        slug: product.slug,
        productId: product.id,
        productVersionId: product.versionId,
        specificationId: profile.specificationId,
        configuration: normalizeConfiguration(product, configuration),
        contact: normalizeCustomizationContact({ ...existingContact, ...(contact || {}) }),
        updatedAt: new Date().toISOString(),
      }
      this.persist()
    },
    saveContact(product: Product, contact: CustomizationContact) {
      if (!this.draft) return
      if (this.draft.slug !== product.slug || this.draft.productVersionId !== product.versionId) return
      this.draft.contact = normalizeCustomizationContact(contact)
      this.draft.updatedAt = new Date().toISOString()
      this.persist()
    },
    clear() {
      this.draft = null
      this.persist()
    },
  },
})
